<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import noResult from '@/assets/images/noResults.png'
import noItens from '@/assets/images/noItens.png'
import emptyStateLogo from '@/assets/icons/white/business.svg'
import StatusRequestsLine from './StatusRequestsLine.vue'
import CardOptions from '@/components/CardOptions.vue'

type ProducerLinkStatus = 'active' | 'pending'
type LgpdRequestStatus = 'none' | 'report' | 'edit' | 'delete'

export type FavoriteProducerRow = {
  id: string
  name: string
  status: ProducerLinkStatus
  request: LgpdRequestStatus
  requests?: LgpdRequestStatus[]
  companyLogo?: string
  logoUrl?: string
  linkedAt?: string | null
  eventName?: string | null
}

const props = withDefaults(
  defineProps<{
    items: FavoriteProducerRow[]
    total: number
    search: string
    page: number
    perPage: number
    loading?: boolean
    perPageOptions?: number[]
    variant?: 'default' | 'mobile'
  }>(),
  {
    items: () => [],
    total: 0,
    search: '',
    page: 1,
    perPage: 5,
    loading: false,
    perPageOptions: () => [5, 10, 25],
    variant: 'default',
  }
)

const emit = defineEmits<{
  (e: 'update:search', v: string): void
  (e: 'update:page', v: number): void
  (e: 'update:per-page', v: number): void
  (e: 'action', payload: { action: string; item: FavoriteProducerRow }): void
}>()

const { mobile } = useDisplay()
const { t } = useI18n()

const logoErrorById = ref<Record<string, boolean>>({})

const isMobile = computed(() => mobile.value)
const isMobileLayout = computed(
  () => props.variant === 'mobile' || isMobile.value
)

const hasSearch = computed(() => !!props.search?.trim())

const isEmptyInitial = computed(() => {
  if (props.loading) return false
  return !hasSearch.value && (props.total ?? 0) === 0
})

const isEmptySearch = computed(() => {
  if (props.loading) return false
  return hasSearch.value && props.items.length === 0
})

const pageCount = computed(() => {
  const per = props.perPage > 0 ? props.perPage : 1
  return Math.max(1, Math.ceil((props.total || 0) / per))
})

const rangeLabel = computed(() => {
  if (!props.total) return '0-0 de 0'
  const start = (props.page - 1) * props.perPage + 1
  const end = Math.min(props.total, props.page * props.perPage)
  return `${start}-${end} de ${props.total}`
})

function setPage(p: number) {
  const safe = Math.min(Math.max(1, p), pageCount.value)
  emit('update:page', safe)
}

function setPerPage(v: unknown) {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return
  emit('update:per-page', n)
  emit('update:page', 1)
}

function setSearch(v: unknown) {
  emit('update:search', String(v ?? ''))
  emit('update:page', 1)
}

function emitAction(action: string, item: FavoriteProducerRow) {
  emit('action', { action, item })
}

watch(
  () => [props.total, props.perPage],
  () => {
    const maxPage = pageCount.value
    if (props.page > maxPage) emit('update:page', maxPage)
  }
)

function getRequestKinds(p: FavoriteProducerRow) {
  const fromArray = (p.requests ?? []).filter(
    (r): r is Exclude<LgpdRequestStatus, 'none'> => r !== 'none'
  )

  if (fromArray.length) return fromArray

  if (p.request && p.request !== 'none') {
    return [p.request as Exclude<LgpdRequestStatus, 'none'>]
  }

  return []
}

function hasRequest(
  p: FavoriteProducerRow,
  kind: Exclude<LgpdRequestStatus, 'none'>
) {
  return getRequestKinds(p).includes(kind)
}

function isAbsoluteUrl(u: string): boolean {
  return /^(https?:)?\/\//i.test(u) || /^blob:|^data:/i.test(u)
}

function toAbsoluteFromResources(u: string): string {
  if (isAbsoluteUrl(u)) return u

  const base =
    (import.meta.env.VITE_API_URL as string | undefined) ??
    'http://localhost:8080'

  return `${base.replace(/\/$/, '')}/resources/${u.replace(/^\//, '')}`
}

function getRowLogoSrc(row: FavoriteProducerRow): string {
  if (logoErrorById.value[row.id]) return emptyStateLogo

  const raw = row.companyLogo ?? row.logoUrl
  if (!raw) return emptyStateLogo

  return toAbsoluteFromResources(raw)
}

function onRowLogoError(id: string) {
  logoErrorById.value = { ...logoErrorById.value, [id]: true }
}
</script>

<template>
  <v-sheet
    class="fav-shell"
    :class="isMobileLayout ? 'fav-shell--mobile' : ''"
    color="white"
    elevation="0"
  >
    <div class="fav-top" :class="isMobileLayout ? 'fav-top--mobile' : ''">
      <v-text-field
        :model-value="search"
        :placeholder="t('general.searchButton')"
        variant="underlined"
        density="comfortable"
        hide-details
        clearable
        append-inner-icon="mdi-magnify"
        class="fav-search"
        :disabled="loading"
        @update:model-value="setSearch"
      />
    </div>

    <v-divider />

    <div class="fav-list" :class="isMobileLayout ? 'fav-list--mobile' : ''">
      <template v-if="loading">
        <div class="fav-loading">{{ t('general.loading') }}</div>
      </template>

      <template v-else-if="isEmptySearch || isEmptyInitial">
        <div class="empty-state--full">
          <div class="empty-content">
            <div class="empty-visual">
              <img
                :src="isEmptySearch ? noResult : noItens"
                :alt="
                  isEmptySearch
                    ? 'No results illustration'
                    : 'Empty illustration'
                "
              />
            </div>

            <h3 class="empty-title">
              {{
                isEmptySearch
                  ? t('eveLink.emptyStateEveLinkRelation')
                  : t('general.nothingHere')
              }}
            </h3>
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="rowItem in items"
          :key="rowItem.id"
          class="fav-row"
          :class="isMobileLayout ? 'fav-row--mobile' : ''"
        >
          <div class="fav-left">
            <div class="fav-avatar" aria-hidden="true">
              <v-img
                class="fav-avatar__img"
                :src="getRowLogoSrc(rowItem)"
                width="32"
                height="32"
                cover
                :alt="rowItem.name"
                @error="onRowLogoError(rowItem.id)"
              />
            </div>

            <div class="fav-content">
              <div class="fav-title" :title="rowItem.name">
                {{ rowItem.name }}
              </div>

              <StatusRequestsLine
                :status="rowItem.status"
                :requests="getRequestKinds(rowItem)"
                :is-mobile="isMobileLayout"
              />
            </div>
          </div>

          <div class="fav-actions">
            <CardOptions
              :id="`favorite-producer-${rowItem.id}`"
              :disabled="loading"
            >
              <v-list density="comfortable" variant="text">
                <v-list-item @click="emitAction('more-info', rowItem)">
                  <v-list-item-title>
                    {{ t('eveLink.guestFavoriteMenu.moreInformation') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="!hasRequest(rowItem, 'report')"
                  @click="emitAction('request-report', rowItem)"
                >
                  <v-list-item-title>
                    {{ t('eveLink.guestFavoriteMenu.requestDataReport') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="!hasRequest(rowItem, 'edit')"
                  @click="emitAction('request-edit', rowItem)"
                >
                  <v-list-item-title>
                    {{ t('eveLink.guestFavoriteMenu.requestDataEdit') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="!hasRequest(rowItem, 'delete')"
                  class="text-error"
                  @click="emitAction('request-delete', rowItem)"
                >
                  <v-list-item-title>
                    {{ t('eveLink.guestFavoriteMenu.requestTotalDelete') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </CardOptions>
          </div>
        </div>
      </template>
    </div>

    <template
      v-if="!loading && !isEmptyInitial && !isEmptySearch && pageCount > 1"
    >
      <div v-if="isMobileLayout" class="fav-pagination-mobile">
        <v-pagination
          :model-value="page"
          :length="pageCount"
          :total-visible="3"
          density="comfortable"
          rounded="circle"
          @update:model-value="setPage"
        />
      </div>

      <div v-else class="fav-footer">
        <div class="fav-footer__left">
          <span class="fav-muted">{{ t('general.itemsPerPage') }}</span>

          <v-select
            :model-value="perPage"
            :items="perPageOptions"
            variant="outlined"
            density="compact"
            hide-details
            class="fav-perpage"
            @update:model-value="setPerPage"
          />
        </div>

        <div class="fav-footer__right">
          <span class="fav-muted">{{ rangeLabel }}</span>

          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            density="comfortable"
            :disabled="page <= 1"
            @click="setPage(page - 1)"
          />

          <v-btn
            icon="mdi-chevron-right"
            variant="text"
            density="comfortable"
            :disabled="page >= pageCount"
            @click="setPage(page + 1)"
          />
        </div>
      </div>
    </template>
  </v-sheet>
</template>

<style scoped>
.fav-shell {
  width: 100%;
  background: #fff;
  border: 1px solid #d7d7d7;
  border-radius: 16px;
}
.fav-shell--mobile {
  border: 0 !important;
  border-radius: 0 !important;
  overflow: visible !important;
}

.fav-top {
  padding: 16px 24px 8px 24px;
}

.fav-top--mobile {
  padding: 12px 16px 8px 16px;
}

.fav-search {
  width: 420px;
  max-width: 420px;
}

@media (max-width: 600px) {
  .fav-search {
    width: 100%;
    max-width: 100%;
  }
}

.fav-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 0 24px;
  height: 65vh;
}

.fav-list--mobile {
  padding: 0 16px !important;
  height: auto !important;
  overflow: visible !important;
}

.fav-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 0;
  transition: background-color 0.2s ease;
}

.fav-row + .fav-row {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.fav-row--mobile {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 0;
}

.fav-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}

.fav-avatar {
  width: 32px;
  height: 32px;
  margin-top: 4px;
  border-radius: 4px;
  background: transparent;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  overflow: hidden;
}

.fav-row--mobile .fav-avatar {
  width: 32px;
  height: 32px;
  margin-top: 4px;
  border-radius: 4px;
  background: transparent;
}

.fav-avatar__img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.fav-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.fav-row--mobile .fav-content {
  min-width: 0;
  padding-top: 0;
}

.fav-title {
  overflow: hidden;
  color: rgba(0, 0, 0, 0.87);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.08px;
}

.fav-row--mobile .fav-title {
  white-space: normal;
  display: -webkit-box;

  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 18px;
  line-height: 24px;
}

.fav-actions {
  flex: 0 0 auto;
  align-self: flex-start;
}

.fav-dots {
  margin-top: -4px;
}

.fav-footer {
  padding: 12px 24px 16px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
}

.fav-footer__left,
.fav-footer__right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fav-muted {
  opacity: 0.7;
  font-size: 14px;
}

.fav-perpage {
  width: 86px;
}

.fav-loading {
  padding: 16px 0;
  opacity: 0.7;
  font-size: 14px;
}

.empty-state--full {
  width: 100%;
  background: #fff;
  padding: 16px 16px 24px 16px;
  box-sizing: border-box;
  min-height: calc(100vh - 320px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: left;
  max-width: 560px;
  width: 100%;
}

.empty-visual {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
}

.empty-visual img {
  max-width: min(420px, 80vw);
  height: auto;
  display: block;
}

.empty-title {
  color: #000;
  font-family: 'Open Sans', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
}

.fav-pagination-mobile {
  padding: 16px 16px 20px 16px;
  display: flex;
  justify-content: center;
}

.fav-row--mobile :deep(.status-requests-line),
.fav-row--mobile :deep(.fav-sub) {
  min-width: 0;
  flex-wrap: wrap;
}

.fav-row--mobile :deep(.fav-sub__status),
.fav-row--mobile :deep(.fav-sub__request) {
  white-space: normal;
}

.request-delete-label {
  color: #f44336;
}
</style>
